/**
 * computeWorldTransform — pure function that derives the world-container
 * scale + translate for a given canvas + camera state.
 *
 * Camera viewport (`camera.width`, `camera.height`) is the "intended
 * frame" the player should see — what one screen of game-space covers
 * (1280×720 by default, matching DevConfig.game.{width,height}). The
 * canvas can be any size (Pixi uses `resizeTo: window`).
 *
 * Strategy: **fit-to-letterbox** — pick the smaller of (canvas/camera)
 * ratios so the entire intended frame fits inside the canvas; center
 * the result so any leftover space sits as black bars on the sides
 * (pillarbox) or top/bottom (letterbox).
 *
 *   ─────────────────────────────────────
 *   │█████│                       │█████│
 *   │█████│  intended frame       │█████│
 *   │█████│  (camera viewport)    │█████│
 *   │█████│                       │█████│
 *   ─────────────────────────────────────
 *      ↑                              ↑
 *   pillar-box                  pillar-box
 *   (canvas wider than camera aspect)
 *
 * Player at world-position (scrollX + camera.width/2, scrollY +
 * camera.height/2) maps to the visual canvas center — independent of
 * canvas size. CameraFollowLogic's deadzone math (using camera.width/2
 * as the screen center) is correct because the render layer makes the
 * camera-center coincide with the visual canvas center.
 *
 * 2026-05-12 — added to fix the camera "unlocked from intended frame"
 * regression. Previously the SubstrateCamera viewport was hardcoded to
 * 1280×720 with no scaling, so on a 1920×1080 window the player
 * appeared off-center by (1920-1280)/2 = 320px.
 */

export interface CanvasSize {
  /** Logical pixel width (Pixi's `app.screen.width`, not DPR-scaled). */
  width: number;
  /** Logical pixel height (Pixi's `app.screen.height`, not DPR-scaled). */
  height: number;
}

export interface CameraState {
  /** Camera viewport width in world units (the intended frame width). */
  width: number;
  /** Camera viewport height in world units (the intended frame height). */
  height: number;
  /** Scroll position (world-space top-left of viewport). */
  scrollX: number;
  scrollY: number;
  /** User zoom (1 = identity, >1 = zoomed in). Optional. */
  zoom?: number;
}

export interface WorldTransform {
  /** Combined scale: fit-to-letterbox × user-zoom. */
  scale: number;
  /** Letterbox offset X (centers the rendered frame inside the canvas). */
  offsetX: number;
  /** Letterbox offset Y (centers the rendered frame inside the canvas). */
  offsetY: number;
  /** Final x translation for the world container (offset minus scaled scroll). */
  worldX: number;
  /** Final y translation for the world container (offset minus scaled scroll). */
  worldY: number;
}

/**
 * Compute the world-container transform.
 *
 * Defensive against zero/negative dims — returns a safe identity transform
 * if either canvas or camera has invalid size (avoids NaN/Infinity from
 * division by zero during the boot window).
 */
export function computeWorldTransform(canvas: CanvasSize, camera: CameraState): WorldTransform {
  if (canvas.width <= 0 || canvas.height <= 0 || camera.width <= 0 || camera.height <= 0) {
    return { scale: 1, offsetX: 0, offsetY: 0, worldX: 0, worldY: 0 };
  }

  const sx = canvas.width / camera.width;
  const sy = canvas.height / camera.height;
  const fitScale = Math.min(sx, sy);
  const userZoom = (camera.zoom && camera.zoom > 0) ? camera.zoom : 1;
  const scale = fitScale * userZoom;

  // The "rendered frame" is camera.width × camera.height × scale logical px.
  // Center it inside the canvas.
  const renderedW = camera.width * scale;
  const renderedH = camera.height * scale;
  const offsetX = (canvas.width - renderedW) / 2;
  const offsetY = (canvas.height - renderedH) / 2;

  // World x/y for the world container: translate by offset (letterbox),
  // then subtract the scaled scroll so the camera-viewport's world-space
  // top-left maps to the letterbox top-left.
  const worldX = offsetX - camera.scrollX * scale;
  const worldY = offsetY - camera.scrollY * scale;

  return { scale, offsetX, offsetY, worldX, worldY };
}

/**
 * Inverse of {@link computeWorldTransform} — map a canvas-logical-pixel
 * point back to world space.
 *
 * The forward transform renders a world point `w` to canvas-logical pixel
 * `s = worldOrigin + w * scale`. Inverting: `w = (s - worldOrigin) / scale`,
 * which expands to `w = scrollX + (s - offsetX) / scale`.
 *
 * Inputs are CANVAS-LOGICAL pixels (the same units as `app.screen` /
 * `window.innerWidth` — NOT device pixels, NOT viewport `clientX/Y` before
 * the canvas-rect subtraction). Callers must subtract the canvas's
 * `getBoundingClientRect().left/top` from `clientX/Y` first.
 *
 * This is the conversion the editor's screen→world hit-test needs:
 * `SubstrateCamera.getWorldPoint` only divides by `zoom` and ignores the
 * fit-to-letterbox scale + offset, so on any window whose logical size
 * differs from the camera's intended frame (≈ every real window) it drifts.
 *
 * Defensive: a degenerate transform (zero canvas/camera) collapses to the
 * identity returned by `computeWorldTransform`, so the result is just the
 * scroll-shifted input — finite, never NaN.
 */
export function screenToWorldPoint(
  screen: { x: number; y: number },
  canvas: CanvasSize,
  camera: CameraState,
): { x: number; y: number } {
  const t = computeWorldTransform(canvas, camera);
  return {
    x: camera.scrollX + (screen.x - t.offsetX) / t.scale,
    y: camera.scrollY + (screen.y - t.offsetY) / t.scale,
  };
}
